// TelcoTrack — Věcná břemena: podpisové sloty dle CharOsKod (port n8n / buildHlavickaVlastnik).

import { splitSjmJointDisplayName } from '../constants/acquisition-contract.constants';
import { krasneJmeno, trimOrEmpty } from './bremena-text.util';
import type { BremenaOwnerInput } from './bremena-contract.types';

export interface BremenaSignerSlot {
  label: string;
}

/** Dokumentové stavy, ve kterých lze přidávat podpisy. */
export const BREMENA_DOCUMENT_SIGNABLE_STATUSES = [
  'schvalen_k_podpisu',
  'castecne_podepsan',
] as const;

export type BremenaDocumentSignableStatus = (typeof BREMENA_DOCUMENT_SIGNABLE_STATUSES)[number];

function charOsKod(owner: BremenaOwnerInput): string {
  return trimOrEmpty(owner.CharOsKod);
}

/** Jména podpisujících pro jednoho vlastníka — stejná logika jako n8n / `buildHlavickaVlastnik`. */
export function resolveSignatoryNamesForOwner(owner: BremenaOwnerInput): string[] {
  const rawJmeno = trimOrEmpty(owner.Jmeno);
  const zastupci = trimOrEmpty(owner.VybraniZastupci);
  if (zastupci) {
    return zastupci
      .split(',')
      .map((part) => krasneJmeno(part.trim()))
      .filter(Boolean);
  }
  if (charOsKod(owner) === '1') {
    const spouses = splitSjmJointDisplayName(rawJmeno);
    if (spouses) return [krasneJmeno(spouses[0]), krasneJmeno(spouses[1])];
  }
  const jmeno = krasneJmeno(rawJmeno);
  return jmeno ? [jmeno] : [];
}

/** Požadované podpisové sloty pro sadu vlastníků nástroje. */
export function requiredSignerSlots(owners: BremenaOwnerInput[]): BremenaSignerSlot[] {
  const labels: string[] = [];
  for (const owner of owners) {
    labels.push(...resolveSignatoryNamesForOwner(owner));
  }
  if (labels.length === 0) {
    return [{ label: 'Vlastník' }];
  }
  return labels.map((label) => ({ label }));
}

export function documentAllowsSigning(status: string | null | undefined): boolean {
  const key = (status ?? '').trim();
  return (BREMENA_DOCUMENT_SIGNABLE_STATUSES as readonly string[]).includes(key);
}

function normalizeSignerName(value: string | null | undefined): string {
  return (value ?? '').trim().toLocaleLowerCase('cs');
}

/** Porovnání jména podpisu se slotem (cs, bez diakritiky není nutná — locale compare). */
export function signerNameMatchesSlot(
  slotLabel: string,
  signerName: string | null | undefined,
): boolean {
  const signed = normalizeSignerName(signerName);
  const slot = normalizeSignerName(slotLabel);
  if (!signed || !slot) return false;
  if (signed === slot) return true;
  return signed.includes(slot) || slot.includes(signed);
}

export function allRequiredSignersPresent(
  owners: BremenaOwnerInput[],
  signatures: Array<{ signerName: string | null | undefined }>,
): boolean {
  const slots = requiredSignerSlots(owners);
  const used = new Set<number>();
  for (const sig of signatures) {
    const idx = slots.findIndex(
      (slot, i) => !used.has(i) && signerNameMatchesSlot(slot.label, sig.signerName),
    );
    if (idx >= 0) used.add(idx);
  }
  return used.size >= slots.length;
}
